import { currency } from '@cred/shared/formatters'
import { extractNumber } from '@cred/shared/utils'
import querystring from 'querystring'
import { fireGtmCtaClick } from 'utils/gtm'
import config from 'config'

import {
  PURCHASE_TYPE,
  REFINANCE_TYPE,
  DOWN_PAYMENT_DEFAULT,
  ESTIMATED_VALUE_DEFAULT,
  HOME_VALUE_DEFAULT,
  LOAN_AMOUNT_DEFAULT,
  ZIP_CODE_DEFAULT,
  EXCELLENT_FICO_TYPE,
  SECOND_HOME_OCCUPANCY_TYPE,
  SINGLE_FAMILY_PROPERTY_TYPE,
  ELIGIBILITY_UUID_PURCHASE,
  ELIGIBILITY_UUID_REFINANCE,
  ELIGIBILITY_LOAN_TYPE,
  MORTGAGE_FILTER_FORM_NAME,
  MORTGAGE_SORTING_FORM_NAME,
  APR_SORTING_OPTION,
  INITIAL_VALUES_BASE_KEYS,
  DEFAULT_TERMS_ARR,
  TERMS_LIMIT,
} from './constants'
import {
  propertyTypes,
  occupancyTypes,
  ficoTypes,
  sortingDropdown,
  termsHeaderLabel,
  scoresMap,
} from './options'

const getValidLoanType = loanType => [PURCHASE_TYPE, REFINANCE_TYPE].some(type => type === loanType) ? loanType : undefined

const isObjectWithKeys = obj => typeof obj === 'object' && !Array.isArray(obj) && Object.keys(obj).length > 0

const stringToBoolean = str => {
  switch (str) {
    case 'true':
      return true
    case 'false':
    default:
      return false
  }
}

const getValidParam = (value, params) => {
  if (value && params.some(param => param.value === value)) return value
  return undefined
}

const getValidSort = value => getValidParam(value, sortingDropdown)

const getParamWithCurrency = value => value && typeof value === 'number'
  ? currency(value)
  : undefined

const getPartnerInitialValues = (values = {}) => {
  const {
    ficoType,
    loanAmount,
    occupancyType,
    propertyType,
    homeValue,
    estimatedValue,
    downPayment,
    zipCode,
    cashOut,
  } = values

  const initialValues = {
    ficoType: getValidParam(ficoType, ficoTypes),
    occupancyType: getValidParam(occupancyType, occupancyTypes),
    propertyType: getValidParam(propertyType, propertyTypes),
    loanAmount: getParamWithCurrency(loanAmount),
    homeValue: getParamWithCurrency(homeValue),
    estimatedValue: getParamWithCurrency(estimatedValue),
    downPayment: getParamWithCurrency(downPayment),
    zipCode: zipCode ? `${zipCode}` : undefined,
    zipCodeRefi: zipCode ? `${zipCode}` : undefined,
    cashOut: getParamWithCurrency(cashOut),
  }

  const filteredValues = Object.entries(initialValues).filter(([, value]) => value)

  return filteredValues.length
    ? filteredValues.reduce((acc, item) => ({ ...acc, ...{ [item[0]]: item[1] } }), {})
    : null
}

const getVariables = (partnerFormData, loanType) => {
  const payments = loanType === PURCHASE_TYPE ? {
    ficoType: partnerFormData.ficoType || EXCELLENT_FICO_TYPE,
    occupancyType: partnerFormData.occupancyType || SECOND_HOME_OCCUPANCY_TYPE,
    propertyType: partnerFormData.propertyType || SINGLE_FAMILY_PROPERTY_TYPE,
    downPayment: partnerFormData.downPayment || DOWN_PAYMENT_DEFAULT,
    estimatedValue: partnerFormData.estimatedValue || ESTIMATED_VALUE_DEFAULT,
    zipCode: partnerFormData.zipCode || ZIP_CODE_DEFAULT,
  } : {
    ficoTypeRefi: partnerFormData.ficoType || EXCELLENT_FICO_TYPE,
    occupancyTypeRefi: partnerFormData.occupancyType || SECOND_HOME_OCCUPANCY_TYPE,
    propertyTypeRefi: partnerFormData.propertyType || SINGLE_FAMILY_PROPERTY_TYPE,
    homeValue: partnerFormData.homeValue || HOME_VALUE_DEFAULT,
    loanAmount: partnerFormData.loanAmount || LOAN_AMOUNT_DEFAULT,
    zipCodeRefi: partnerFormData.zipCode || ZIP_CODE_DEFAULT,
  }
  return {
    values: {
      ...payments,
      vertical: loanType,
      loanType,
    },
  }
}

const parseInputFromFormData = ({ values }) => {
  const {
      loanType: vertical,
      ficoType,
      ficoTypeRefi,
      zipCode,
      zipCodeRefi,
      estimatedValue,
      downPayment,
      homeValue,
      loanAmount,
      occupancyType,
      occupancyTypeRefi,
      propertyType,
      propertyTypeRefi,
      purchaseUuid,
      refiUuid,
      cashOut,
    } = values,
    isPurchase = vertical === PURCHASE_TYPE

  return {
    vertical,
    uuid: isPurchase ? purchaseUuid : refiUuid,
    ficoType: isPurchase ? ficoType : ficoTypeRefi,
    zipCode: isPurchase ? zipCode : zipCodeRefi,
    estimatedValue: isPurchase ? extractNumber(estimatedValue) : null,
    downPayment: isPurchase ? extractNumber(downPayment) : null,
    homeValue: !isPurchase ? extractNumber(homeValue) : null,
    loanAmount: !isPurchase ? extractNumber(loanAmount) : null,
    occupancyType: isPurchase ? occupancyType : occupancyTypeRefi,
    propertyType: isPurchase ? propertyType : propertyTypeRefi,
    cashOut: !isPurchase ? extractNumber(cashOut) : null,
  }
}

const getPrefillQueryKeyFromUuid = uuid => uuid ? 'mortgageWidgetSubmission' : 'mortgageWidgetPrefillSubmission'
const getPurchaseUuidFromLocalStorage = () => {
  try {
    return localStorage.getItem(ELIGIBILITY_UUID_PURCHASE)
  } catch (e) {
    return undefined
  }
}
const getRefiUuidFromLocalStorage = () => {
  try {
    return localStorage.getItem(ELIGIBILITY_UUID_REFINANCE)
  } catch (e) {
    return undefined
  }
}
const getLoanTypeFromLocalStorage = () => {
  try {
    return localStorage.getItem('loanType')
  } catch (e) {
    return undefined
  }
}
const storeSubmissionInLocalStorage = (uuid, loanType) => {
  try {
    if (loanType === PURCHASE_TYPE) {
      localStorage.setItem(ELIGIBILITY_UUID_PURCHASE, uuid)
    } else {
      localStorage.setItem(ELIGIBILITY_UUID_REFINANCE, uuid)
    }

    localStorage.setItem(ELIGIBILITY_LOAN_TYPE, loanType)
  } catch (e) {
    return undefined
  }
}

/* eslint-disable camelcase */
const generateOnGetStartedCallback = ({ utmParams, isPurchase, omitUtmParams, metaQuery }) => {
  const query = omitUtmParams ? '' : `?${querystring.encode(utmParams)}`

  if (isPurchase) {
    return () => {
      // Fire custom GTM event for Segment Track call
      fireGtmCtaClick()

      window.open(`${config.credibleRoot}/mortgage/signup${query}${metaQuery || ''}`)
    }
  }

  return () => {
    // Fire custom GTM event for Segment Track call
    fireGtmCtaClick()

    window.open(`${config.credibleRoot}/mortgage-refinance/signup${query}${metaQuery || ''}`)
  }
}

const getTermsHeaderLabel = (terms, termsList = DEFAULT_TERMS_ARR) => {
  // tmp disabled arm
  // const t = terms.length ? terms : [THIRTY_YEARS_FIXED, FIFTEEN_YEARS_FIXED, FIVE_ONE_ARM]
  const t = terms.length ? terms : termsList
  const label = t.reduce((currText, nextTerm) => `${currText ? `${currText}, ` : ''} ${termsHeaderLabel[nextTerm]}`, '')
  return label.trim()
}

/**
 *
 * @param {Object} terms - An object with term names for keys booleans as value
 * @return {[]} - Returns a list of term names
 */
const getTerms = (terms) => Object.entries(terms)
  .filter(v => v[1])
  .map(v => v[0])

/**
 * Function used to parse current form  state and render the values being filtered to the user. This updates in three
 * instances. When a form has been submitted, when the loanType value changes, and when the term changes.
 * @param {Object} formValues - redux form values property
 * @param {Object} termValues - terms field in the FilterForm
 * @param {Boolean} isPurchase - loanType tracker of purchase vs. refi
 */
const getFilterText = (formValues, termValues, isPurchase) => {
  const {
    estimatedValue,
    homeValue,
    downPayment,
    loanAmount,
    zipCode,
    zipCodeRefi,
    ficoType,
    ficoTypeRefi,
    terms: termsList,
  } = formValues

  const currentFico = isPurchase ? ficoType : ficoTypeRefi
  const ficoTypePlaceholder = ficoTypes.find(({ value }) => currentFico === value)
  const terms = getTerms(termValues)
  const typeOfLoan = isPurchase ? ' loan' : ''
  const value = isPurchase ? estimatedValue : homeValue
  const amount = isPurchase ? downPayment : loanAmount
  const action = isPurchase ? 'down' : 'loan'
  const zip = isPurchase ? zipCode : zipCodeRefi
  const creditScore = ficoTypePlaceholder && scoresMap[ficoTypePlaceholder.value]

  if (value && amount && creditScore) {
    return `${getTermsHeaderLabel(terms, termsList)}${typeOfLoan} for ${value} in ${zip} with ${amount} ${action} and ${creditScore} credit score.`
  }

  return 'Searching...'
}

const getLoanTerm = (obj) => {
  if (!obj) return
  const result = Object.entries(obj)
    .filter(([, value]) => value)
    .map(([item]) => termsHeaderLabel[item])
  return result.length > 1 ? result.join(', ') : result.join()
}

const getSnowplowFilter = (formData, term) => {
  const {
    vertical,
    ficoType,
    zipCode,
    estimatedValue,
    downPayment,
    homeValue,
    loanAmount,
    occupancyType,
    propertyType,
  } = parseInputFromFormData(formData)

  return `${vertical === 'purchase' ? `${estimatedValue}, ${downPayment}` : `${homeValue}, ${loanAmount}`} , ${zipCode}, ${occupancyType}, ${propertyType}, ${ficoType}, ${vertical}, ${getLoanTerm(term)}`
}

const expandTermsArrToLimit = (value) => {
  const filteredDefaultTerms = DEFAULT_TERMS_ARR.filter((term) => !value.includes(term)).slice(0, TERMS_LIMIT - value.length)
  return [...value, ...filteredDefaultTerms]
}

const getValidTermsArr = terms => {
  if (!terms || !terms.length) {
    return DEFAULT_TERMS_ARR
  }

  const termsArr = terms.split(',')

  if (termsArr.length < TERMS_LIMIT) {
    return expandTermsArrToLimit(termsArr)
  }

  if (termsArr.length > TERMS_LIMIT) {
    return termsArr.slice(0, TERMS_LIMIT)
  }

  return termsArr
}

const getValidTermDataState = (termsArr, options = {}) => {
  const stateFromPartnerFormData = Object.keys(options).length && Object.keys(options)
    .filter((element) => termsArr.includes(element))
    .reduce((acc, element) => ({ ...acc, [element]: stringToBoolean(options[element]) }), {})

  return Object.keys(stateFromPartnerFormData).length ? stateFromPartnerFormData : { [termsArr[0]]: true }
}

const mortgageWidgetMapStateToProps = (state, ownProps) => {
  const { initialValues: partnerFormData = {} } = ownProps,
    { loanType, sorting, terms, options } = partnerFormData,
    formData = state.form[MORTGAGE_FILTER_FORM_NAME],
    sortingInitialValue = getValidSort(sorting) || APR_SORTING_OPTION,
    sortingFormData = state.form[MORTGAGE_SORTING_FORM_NAME],
    sortingFormValue = sortingFormData && sortingFormData.values ? sortingFormData.values.sorting : null,
    termFormValue = formData && formData.values ? formData.values.term : null,
    loanTypeInitialValue = getValidLoanType(loanType) || getLoanTypeFromLocalStorage() || REFINANCE_TYPE,
    loanTypeFormValue = formData && formData.values ? formData.values.loanType : null,
    loanTypeState = loanTypeFormValue || loanTypeInitialValue,
    isPurchase = loanTypeState === PURCHASE_TYPE,
    termsArrInitialValue = getValidTermsArr(terms),
    termInitialValue = getValidTermDataState(termsArrInitialValue, options)

  return {
    formData,
    partnerFormData,
    initialValues: {
      term: termInitialValue,
      terms: termsArrInitialValue,
      sorting: sortingInitialValue,
      loanType: loanTypeInitialValue,
      ...getPartnerInitialValues(partnerFormData),
    },
    sortBy: sortingFormValue || sortingInitialValue,
    termState: termFormValue || termInitialValue,
    loanTypeState,
    isPurchase,
    page: state.components && state.components.pagination && state.components.pagination['mortgage-rate-table'],
  }
}

/**
 *
 * @param {Object} formData - An object with initial values, setted by partner
 * @return {Boolean} - Returns true if we have values that affect query result
 */

const checkForAffectKeys = (formData) => Object.keys(formData).some(element => !INITIAL_VALUES_BASE_KEYS.includes(element))


export {
  getValidLoanType,
  getPartnerInitialValues,
  getVariables,
  getTermsHeaderLabel,
  isObjectWithKeys,
  getValidSort,
  parseInputFromFormData,
  getPrefillQueryKeyFromUuid,
  getPurchaseUuidFromLocalStorage,
  getRefiUuidFromLocalStorage,
  storeSubmissionInLocalStorage,
  getLoanTypeFromLocalStorage,
  generateOnGetStartedCallback,
  getFilterText,
  getTerms,
  mortgageWidgetMapStateToProps,
  getSnowplowFilter,
  checkForAffectKeys,
}
